'use client'

import type { MouseEvent, ReactNode } from 'react'

import { EmptyState } from '@/app/components/shared/EmptyState'
import { InfiniteVirtualizedList } from '@/app/components/shared/InfiniteVirtualizedList'
import { HorizontalCard } from '@/app/components/shared/cards/HorizontalCard'
import { VerticalCard } from '@/app/components/shared/cards/VerticalCard'
import { TrackedProductCard } from '@/app/components/shared/TrackedProductCard'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { useViewModeContext } from '@/app/context/viewMode'
import { FILTERED_LISTING_MODULE_TYPE } from '@/app/lib/analytics/constants'
import { mapProductToAnalyticsItem } from '@/app/lib/analytics/mapProductToAnalyticsItem'
import { useProductListTracking } from '@/app/lib/analytics/useProductListTracking'
import type { SubcategoryQueryFilters } from '@/app/lib/api/api'
import { isLinkableProduct, mapProductToCard } from '@/app/lib/api/mapProductToCard'
import type { ProductApiItem } from '@/app/lib/api/types-api'
import {
  COLUMN_GAP,
  COLUMN_GAP_PX,
  PAGE_PARAM,
  ROW_GAP_PX,
  SCROLL_THRESHOLD,
} from '@/app/lib/constants'
import type { InfiniteListPageLoadedInfo } from '@/app/lib/hooks/useInfiniteList'
import { useIsDesktopViewport } from '@/app/lib/hooks/useIsDesktopViewport'
import { computeHorizontalColumns, useResponsiveColumns } from '@/app/lib/hooks/useResponsiveColumns'
import { estimateHorizontalRowHeight, estimateRowHeight } from '@/app/lib/utils'

import {
  ANALYTICS_ITEM_LIST_ID,
  ANALYTICS_ITEM_LIST_NAME,
  ANALYTICS_MODULE_NAME,
  ANALYTICS_MODULE_POSITION,
} from '@/app/entradas/(general-web)/[slug]/[slug2]/[slug3]/_components/analyticsConstants'
import { fetchSubcategoriaPageAction } from '@/app/entradas/(general-web)/[slug]/[slug2]/[slug3]/_components/actions'

interface SubcategoriaResultsProps {
  /** Productos de la página inicial, ya obtenidos server-side. */
  readonly initialProducts: ProductApiItem[]
  /** Página con la que arrancó la navegación (1, o la de `?page=N`). */
  readonly initialPage: number
  readonly totalPages: number
  /** `per_page` real de `/data` para esta consulta (sin default compartido). */
  readonly pageSize: number
  readonly categoria: string
  readonly subcategoria: string
  readonly location: string
  readonly filters: SubcategoryQueryFilters
  /** `SeoPaginationLinks` (Server Component) ya resuelto por `page.tsx`. */
  readonly paginationLinks: ReactNode
}

/**
 * `SubcategoriaResults` — adaptador fino de `InfiniteVirtualizedList` (scroll
 * infinito + virtualización + paginación SEO, genérico) con los datos concretos de
 * esta página: `VerticalCard`/`mapProductToCard` para el render de cada evento y
 * `fetchSubcategoriaPageAction` como adaptador de datos. No gestiona su propio
 * reinicio de estado: `page.tsx` la desmonta/remonta con una `key` cuando cambian
 * categoría/subcategoría/location/filtros.
 *
 * La vista horizontal/vertical la controla `ViewModeContext` (compartido con el
 * `VistaToggle` de `SubcategoriaFilters`). Requiere `<ViewModeProvider>` como
 * ancestro. El selector no está disponible en mobile/tablet, así que `isDesktop`
 * fuerza `VerticalCard` ahí con independencia del `viewMode` del contexto.
 */
export function SubcategoriaResults({
  initialProducts,
  initialPage,
  totalPages,
  pageSize,
  categoria,
  subcategoria,
  location,
  filters,
  paginationLinks,
}: SubcategoriaResultsProps) {
  const { viewMode } = useViewModeContext()
  const isDesktop = useIsDesktopViewport()
  const isHorizontal = viewMode === 'horizontal' && isDesktop

  const { columns, estimatedRowHeight } = useResponsiveColumns({
    columnGapPx: COLUMN_GAP_PX,
    estimateRowHeight: isHorizontal ? estimateHorizontalRowHeight : estimateRowHeight,
    columnsOverride: isHorizontal ? computeHorizontalColumns : undefined,
  })

  // ── Analítica de e-commerce ────────────────────────────────────────────────
  // Mismo patrón que `LoMasVendidoResults`. La dedup de impresiones vive en el
  // hook y se reinicia cuando `page.tsx` remonta (`key`) al cambiar filtros.
  // `index` de `renderItem` es ABSOLUTO (lista acumulada), así que el índice de
  // `view_item_list` de cada lote del scroll infinito y el de `select_item` coinciden.
  const analytics = useOptionalPageAnalytics()
  const { trackImpression, trackSelect, trackPurchase } = useProductListTracking(
    {
      itemListId: ANALYTICS_ITEM_LIST_ID,
      itemListName: ANALYTICS_ITEM_LIST_NAME,
      moduleName: ANALYTICS_MODULE_NAME,
      moduleType: FILTERED_LISTING_MODULE_TYPE,
      modulePosition: ANALYTICS_MODULE_POSITION,
    },
    {
      trackViewItemList: analytics?.trackViewItemList,
      trackSelectItem: analytics?.trackSelectItem,
      trackAddToCart: analytics?.trackAddToCart,
    },
  )

  function handlePageLoaded({ itemsLoadedTotal }: InfiniteListPageLoadedInfo) {
    // Carga automática al llegar al sentinela (sin CTA): encaja `view_more`.
    analytics?.trackViewMore({
      module_name: ANALYTICS_MODULE_NAME,
      module_type: FILTERED_LISTING_MODULE_TYPE,
      module_position: ANALYTICS_MODULE_POSITION,
      items_loaded_total: String(itemsLoadedTotal),
    })
  }

  // `pagination` solo para clicks reales en los enlaces de `SeoPaginationLinks`
  // (Server Component ya resuelto, sin `onClick` propio: se delega aquí).
  function handlePaginationClick(event: MouseEvent<HTMLDivElement>) {
    const { target } = event
    if (!(target instanceof Element)) return
    const link = target.closest('a[href]')
    if (!link) return
    const rel = link.getAttribute('rel')
    const href = link.getAttribute('href') ?? ''
    const page = new URLSearchParams(href.split('?')[1] ?? '').get(PAGE_PARAM) ?? '1'
    analytics?.trackPagination({
      pagination_method: rel === 'prev' ? 'prev' : rel === 'next' ? 'next' : 'direct_number',
      page_list_load: page,
      items_per_load: String(pageSize),
      module_name: ANALYTICS_MODULE_NAME,
      module_type: FILTERED_LISTING_MODULE_TYPE,
    })
  }

  const trackedPaginationLinks = <div onClickCapture={handlePaginationClick}>{paginationLinks}</div>

  // `isLinkableProduct` descarta productos sin ficha (PDP) derivable — p. ej.
  // llegan sin `genres` — para no pintar una card sin destino ni en la carga
  // inicial ni en las páginas siguientes del scroll infinito (mismo criterio
  // que `toProductCards`/`toGridCards`, `mapProductToCard.ts`). `totalPages`
  // no se recalcula: viene tal cual del backend, así que filtrar aquí no
  // desincroniza la paginación de `InfiniteVirtualizedList` (solo puede
  // resultar en menos de `pageSize` cards visibles en alguna página).
  return (
    <InfiniteVirtualizedList<ProductApiItem>
      initialItems={initialProducts.filter(isLinkableProduct)}
      initialPage={initialPage}
      totalPages={totalPages}
      pageSize={pageSize}
      threshold={SCROLL_THRESHOLD}
      getItemKey={(item) => item.id}
      fetchPage={async (page) => {
        const result = await fetchSubcategoriaPageAction(categoria, subcategoria, location, filters, page)
        return { ...result, items: result.items.filter(isLinkableProduct) }
      }}
      onPageLoaded={handlePageLoaded}
      renderItem={(item, index) => (
        <TrackedProductCard
          index={index}
          onImpression={() => trackImpression(item.id, mapProductToAnalyticsItem(item, index))}
          onSelect={() => trackSelect(mapProductToAnalyticsItem(item, index))}
          onPurchase={(href) => trackPurchase(mapProductToAnalyticsItem(item, index), href)}
        >
          {isHorizontal ? (
            <HorizontalCard {...mapProductToCard(item)} />
          ) : (
            <VerticalCard {...mapProductToCard(item)} />
          )}
        </TrackedProductCard>
      )}
      estimatedRowHeight={estimatedRowHeight}
      columns={columns}
      columnGap={COLUMN_GAP}
      rowGap={ROW_GAP_PX}
      pageParam={PAGE_PARAM}
      paginationLinks={trackedPaginationLinks}
      errorMessage="No se han podido cargar más eventos."
      loadingLabel="Cargando más eventos"
      emptyState={
        <EmptyState title="No hay eventos disponibles" description="Prueba a cambiar de provincia o de filtros." />
      }
    />
  )
}
